import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CalendarIcon, CheckCircle2, Phone } from "lucide-react";
import { api, formatApiError, CLINIC } from "../lib/api";
import { SERVICES } from "../data/services";
import { ro } from "date-fns/locale";
import { format } from "date-fns";
import { toast, Toaster } from "sonner";

const TIME_SLOTS = [
  { value: "09:00", label: "09:00 (Marți / Vineri — CAS)" },
  { value: "10:00", label: "10:00 (Luni / Marți / Vineri — CAS)" },
  { value: "11:00", label: "11:00 (Luni / Marți / Vineri — CAS)" },
  { value: "12:00", label: "12:00 (Luni — CAS)" },
  { value: "14:00", label: "14:00 (Miercuri / Joi — CAS)" },
  { value: "15:00", label: "15:00 (Miercuri / Joi — CAS)" },
  { value: "16:00", label: "16:00 (Miercuri / Joi — CAS)" },
  { value: "alt", label: "Alt interval — vă sunăm pentru confirmare" },
];

export default function Booking() {
  const [params] = useSearchParams();
  const initialService = params.get("serviciu") || SERVICES[0].title;

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: initialService,
    preferred_date: null,
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.preferred_date || !form.preferred_time) {
      toast.error("Vă rugăm completați toate câmpurile obligatorii.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        service: form.service,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
        preferred_time: form.preferred_time,
        message: form.message,
      };
      await api.post("/appointments", payload);
      setDone(true);
      toast.success("Cererea de programare a fost trimisă!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Nu am putut trimite cererea.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto container-x py-24 text-center">
        <Toaster position="top-center" />
        <div className="mx-auto w-20 h-20 rounded-full bg-brand-green/15 flex items-center justify-center text-brand-green">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h1 className="mt-6 font-display font-extrabold text-brand-navy text-3xl sm:text-4xl">Vă mulțumim! Cererea a fost trimisă.</h1>
        <p className="mt-4 text-brand-navy/80 text-lg">Veți fi contactat(ă) telefonic în cel mai scurt timp pentru confirmarea programării. În caz de urgență, vă rugăm să ne sunați direct.</p>
        <a href={CLINIC.phoneTel} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold" data-testid="booking-success-call">
          <Phone className="w-5 h-5" /> {CLINIC.phonePretty}
        </a>
      </div>
    );
  }

  return (
    <div>
      <Toaster position="top-center" />
      <section className="bg-soft-cream">
        <div className="max-w-7xl mx-auto container-x py-16">
          <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Programare</p>
          <h1 className="font-display font-extrabold text-brand-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight">Solicită o programare</h1>
          <p className="mt-5 text-brand-navy/75 text-lg max-w-2xl font-light">Completați formularul de mai jos, iar noi vă vom contacta pentru confirmarea exactă a intervalului orar. <strong className="text-brand-navy">Trimiterea formularului nu confirmă automat programarea</strong> — vă contactăm telefonic pentru a stabili împreună un interval potrivit. În caz de urgență, vă rugăm să ne sunați direct: <a href={CLINIC.phoneTel} className="font-bold underline text-brand-red">{CLINIC.phonePretty}</a>.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto container-x py-12">
        <form onSubmit={submit} className="bg-white rounded-3xl p-6 sm:p-10 shadow-soft border border-brand-yellow/40 space-y-5" data-testid="booking-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nume și prenume *" htmlFor="name">
              <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Ex: Maria Popescu" required data-testid="booking-name" />
            </Field>
            <Field label="Telefon *" htmlFor="phone">
              <Input id="phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="07XX XXX XXX" required data-testid="booking-phone" />
            </Field>
          </div>

          <Field label="Email (opțional)" htmlFor="email">
            <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="email@exemplu.ro" data-testid="booking-email" />
          </Field>

          <Field label="Serviciu dorit *" htmlFor="service">
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger data-testid="booking-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Prima consultație">Prima consultație (recomandată pentru pacienți noi)</SelectItem>
                {SERVICES.map((s) => (
                  <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                ))}
                <SelectItem value="Consultație generală">Consultație generală</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Data preferată *" htmlFor="date">
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-start text-left font-normal" data-testid="booking-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.preferred_date ? format(form.preferred_date, "PPP", { locale: ro }) : <span className="text-muted-foreground">Alegeți o dată</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.preferred_date}
                    onSelect={(d) => update("preferred_date", d)}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    locale={ro}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="Ora preferată *" htmlFor="time">
              <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
                <SelectTrigger data-testid="booking-time"><SelectValue placeholder="Alegeți intervalul" /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Mesaj (opțional)" htmlFor="message">
            <Textarea id="message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Detalii utile despre situația dumneavoastră" rows={4} data-testid="booking-message" />
          </Field>

          <div className="bg-brand-cream rounded-2xl p-4 text-sm text-brand-navy/85">
            <strong>Notă:</strong> Programările prin Casa de Asigurări de Sănătate sunt: <strong>Luni 10:00–13:00</strong> · <strong>Marți 09:00–12:00</strong> · <strong>Miercuri 14:00–17:00</strong> · <strong>Joi 10:00–13:00</strong> · <strong>Vineri 10:00–13:00</strong>. Pentru alte intervale orare, vă vom contacta telefonic pentru a stabili împreună o oră potrivită.
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-brand-red hover:bg-brand-redSoft text-white text-base py-6 rounded-full font-semibold" data-testid="booking-submit">
            {submitting ? "Se trimite…" : "Trimite solicitarea de programare"}
          </Button>

          <p className="text-xs text-brand-navy/85 leading-relaxed pt-1" data-testid="booking-confirmation-disclaimer">
            <strong>Important:</strong> Trimiterea acestui formular reprezintă o solicitare de programare și <strong>nu confirmă automat</strong> intervalul ales. Programarea devine confirmată doar după contactul telefonic din partea cabinetului.
          </p>

          {/* GDPR 1.6 — Text informativ lângă butonul de trimitere (fără bifă obligatorie). */}
          <p className="text-xs text-brand-navy/70 leading-relaxed pt-1" data-testid="booking-gdpr-notice">
            Datele furnizate sunt utilizate pentru gestionarea solicitării dumneavoastră de programare și comunicările aferente.
            Pentru detalii, consultați <Link to="/informare-gdpr" className="text-brand-red underline hover:text-brand-navy" data-testid="booking-gdpr-link-info">Informarea GDPR pentru pacienți</Link> și <Link to="/politica-confidentialitate" className="text-brand-red underline hover:text-brand-navy" data-testid="booking-gdpr-link-privacy">Politica de confidențialitate</Link>.
          </p>
        </form>
      </section>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-brand-navy font-semibold">{label}</Label>
      {children}
    </div>
  );
}
